import * as React from "react";
import ReactSelect from "react-select";
import {uniqueId} from "../../config/Tools";
import useTranslation from "../../hooks/useTranslation";
import {useMemo, useRef, useState} from "react";
import "./Select.scss";

function Select(props) {
    const {t} = useTranslation();
    const {
        name,
        label = t(name),
        value,                // očekávám "value" (string/number) z rodiče
        options = [],
        required,
        disabled,
        className: classNameProp,
        onChange,             // (name, value)
        onValidate            // (value) => string|null  (vrátí klíč chyby nebo null)
    } = props;

    const [error, setError] = useState(null);
    const hiddenInputRef = useRef(null);
    const id = useMemo(() => uniqueId(), []);
    const className = [
        "FormSelect FormInput dropdown col-50",
        classNameProp,
        disabled ? "disabled" : "",
        error ? "has-error" : ""
    ].filter(Boolean).join(" ");

    // Najdi selected option podle "value" z rodiče (controlled)
    const selectedOption = useMemo(
        () => options.find(o => o.value === value) ?? null,
        [options, value]
    );

    const runValidation = (val) => {
        // 1) required
        if (required && (val === undefined || val === null || val === "")) {
            return "fieldRequired";
        }
        // 2) custom
        if (onValidate) {
            return onValidate(val) || null;
        }
        return null;
    };

    const applyValidityToHidden = (msgKey) => {
        const input = hiddenInputRef.current;
        if (!input) return;
        if (msgKey) {
            input.setCustomValidity(t(msgKey));
            input.title = t(msgKey); // pro nápovědu
        } else {
            input.setCustomValidity("");
            input.title = "";
        }
    };

    const handleChange = (selected) => {
        const val = selected?.value ?? "";
        // necháme projít user validaci a HTML5 validaci
        const msg = runValidation(val);
        setError(msg);
        applyValidityToHidden(msg);
        onChange && onChange(name, val);

        // okamžitě požádej prohlížeč o kontrolu (užitečné při blur/change)
        hiddenInputRef.current?.reportValidity();
    };

    const handleBlur = () => {
        // při bluru jen ověříme a případně ukážeme bublinu
        hiddenInputRef.current?.reportValidity();
    };

    return (
        <div className={className}>
            {label !== false && <label className="label" htmlFor={id}>{label}</label>}

            {/* Vizuální select */}
            <ReactSelect
                inputId={id}
                isDisabled={disabled}
                options={options}
                value={selectedOption}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder={null}
                aria-invalid={!!error}
                aria-describedby={error ? `${id}-error` : undefined}
                classNamePrefix="react-select"
            />

            {/* Skryté pole kvůli HTML5 validaci a odeslání do <form> */}
            <input
                ref={hiddenInputRef}
                tabIndex={-1}
                name={name}
                value={value ?? ""}
                onChange={() => {
                }}
                required={!!required}
                style={{position: "absolute", opacity: 0, width: 0, height: 0, pointerEvents: "none"}}
                aria-hidden="true"
            />

            {error && <label id={`${id}-error`} className="error">{t(error)}</label>}
        </div>
    );
}

export default Select;
